import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaEdit, FaShieldAlt, FaTrash } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import {
  parseRolePermissions,
  getAssignablePermissionModules,
  permissionKey,
  sanitizeAssignablePermissions,
} from "../../config/teamRolePermissions";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getUserType } from "../../utils/auth";

const TeamRoles = () => {
  const { projectId } = useProjectPhone();
  const userType = getUserType();
  const permissionModules = useMemo(
    () => getAssignablePermissionModules(userType),
    [userType],
  );
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    teamRoleName: "",
    roleid: "",
    extractedalphaid: projectId,
    permissions: {},
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/projects/${projectId}`,
      );
      const project = res?.project || res;
      const list = (project?.teamroles || []).map((role) => ({
        ...role,
        permissions: parseRolePermissions(role),
      }));
      setRoles(list);
    } catch (err) {
      setError(err.message || "Failed to load team roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (roleId) => {
    if (!window.confirm("Delete this team role?")) return;
    setError("");
    setSuccess("");
    try {
      await apihubPost("/getInteractedCustomerDetail/deleteteamrole", {
        roleid: roleId,
        projectid: projectId,
      });
      setSuccess("Team role deleted successfully.");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete team role");
    }
  };

  const openEdit = (role) => {
    setEditForm({
      teamRoleName: role.name || "",
      roleid: role._id,
      extractedalphaid: projectId,
      permissions: { ...parseRolePermissions(role) },
    });
    setEditOpen(true);
  };

  const togglePermission = (key) => {
    setEditForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.teamRoleName.trim()) {
      setError("Please enter a team role name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apihubPost("/getInteractedCustomerDetail/update-team-role", {
        ...editForm,
        permissions: sanitizeAssignablePermissions(
          editForm.permissions,
          userType,
        ),
      });
      setSuccess("Team role updated successfully.");
      setEditOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Failed to update team role");
    } finally {
      setSaving(false);
    }
  };

  const enabledKeys = (perms) =>
    Object.entries(perms || {})
      .filter(([, v]) => v)
      .map(([k]) => k);

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-row">
        <div>
          <h1>
            <FaShieldAlt />
            Team Roles
          </h1>
          <p className="biz-panel-subtitle">
            Manage your team securely with role-based access and controlled
            permissions.
          </p>
        </div>
        <Link
          to={businessPath(projectId, "create-teamsrole")}
          className="biz-btn-primary"
        >
          Create Team Role
        </Link>
      </div>

      {success && <p className="biz-banner biz-banner-success">{success}</p>}
      {error && !editOpen && (
        <p className="biz-banner biz-banner-error">{error}</p>
      )}

      <div className="biz-table-wrap">
        <table className="biz-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Permissions</th>
              <th className="biz-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="biz-table-empty">
                  Loading roles…
                </td>
              </tr>
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={3} className="biz-table-empty">
                  No roles found
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role._id}>
                  <td>{role.name}</td>
                  <td>
                    <ul className="biz-perm-tags">
                      {enabledKeys(role.permissions).map((key) => (
                        <li key={key}>{key}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="biz-col-actions">
                    <div className="biz-table-actions">
                      <button
                        type="button"
                        className="biz-btn-icon"
                        title="Edit"
                        onClick={() => openEdit(role)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="biz-btn-icon biz-btn-icon-danger"
                        title="Delete"
                        onClick={() => onDelete(role._id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editOpen && (
        <div className="biz-modal-backdrop" onClick={() => setEditOpen(false)}>
          <div
            className="biz-modal-card biz-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Edit Team Role</h2>
            {error && <p className="biz-banner biz-banner-error">{error}</p>}
            <form onSubmit={saveEdit}>
              <label>
                Team Role Name *
                <input
                  value={editForm.teamRoleName}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      teamRoleName: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <div className="biz-perm-editor">
                {permissionModules.map((mod) => (
                  <div key={mod.label} className="biz-perm-module">
                    <strong>{mod.label}</strong>
                    <div className="biz-perm-module-actions">
                      {mod.actions.map((action) => {
                        const key = permissionKey(mod.label, action);
                        return (
                          <label key={key} className="biz-perm-check">
                            <input
                              type="checkbox"
                              checked={Boolean(editForm.permissions[key])}
                              onChange={() => togglePermission(key)}
                            />
                            {action}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="biz-modal-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={() => setEditOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="biz-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamRoles;
